<template>
  <div :id="id" :class="wrapperClasses">
    <div class="container mx-auto flex flex-col items-center space-y-2 px-6 md:px-12 lg:flex-row lg:space-x-10">
      <div class="flex w-full flex-col lg:w-2/5">
        <div
          v-t="$cfg.newsletterTitle"
          :class="textColor ? textColor : 'text-white'"
          class="mb-4 text-3xl font-bold text-white lg:text-5xl"
        ></div>
        <div
          :class="textColor ? textColor : 'text-white'"
          class="text-base font-medium leading-tight text-white"
          v-html-safe="$cfg.newsletterSubtitle"
        ></div>
      </div>
      <div class="pt:mt-0 flex w-full grow pt-8 lg:w-3/5">
        <VcInput
          v-model="emailAddress"
          class="max-h-14 grow lg:max-w-[450px]"
          no-border
          input-class="text-white placeholder:text-white"
          bg-class="bg-[color:var(--color-newsletter-input-bg)]"
          :placeholder="$cfg.newsletterPlaceholder"
        />
        <VcIcon
          class="-mt-1 ml-3 cursor-pointer text-primary"
          @click="submitNewsLetter()"
          name="veld-button"
          :size="70"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useThemeContext } from "@/core/composables";
import { SPOTLER_KEY_PROP, SPOTLER_SECRET_PROP } from "@/core/constants";
import { useNotifications } from "@/shared/notification";

interface IProps {
  id: string;
  background?: string;
  textColor?: string;
}

const props = defineProps<IProps>();

const wrapperClasses = computed(() => {
  return [props.background ? props.background : "bg-[color:var(--color-footer-top-bg)]", "py-6 lg:py-10"];
});
const { t } = useI18n();
const notifications = useNotifications();
const { themeContext } = useThemeContext();
const emailAddress = ref("");

const spotlerKey = computed(() => {
  const dpArray = themeContext.value.dynamicProperties ?? [];
  return (dpArray.find((item) => item.name && item.name === SPOTLER_KEY_PROP)?.value as string) ?? "";
});

const spotlerSecret = computed(() => {
  const dpArray = themeContext.value.dynamicProperties ?? [];
  return (dpArray.find((item) => item.name && item.name === SPOTLER_SECRET_PROP)?.value as string) ?? "";
});

// Innovadis custom Nick: Very old Crypto-JS package had to be deleted because of Azure package audit isseus. So this is a new implementation of the OAuth1 signature (using AI)
function percentEncode(value: string) {
  return encodeURIComponent(value).replace(/[!*'()]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
}

async function buildOAuthAuthorizationHeader(url: string, method: string, consumerKey: string, consumerSecret: string) {
  const oauthParams: Record<string, string> = {
    oauth_consumer_key: consumerKey,
    oauth_nonce: crypto.randomUUID().replace(/-/g, ""),
    oauth_signature_method: "HMAC-SHA1",
    oauth_timestamp: Math.floor(Date.now() / 1000).toString(),
    oauth_version: "1.0",
  };

  const baseString = [
    method.toUpperCase(),
    percentEncode(url),
    percentEncode(
      Object.keys(oauthParams)
        .sort()
        .map((key) => `${key}=${percentEncode(oauthParams[key])}`)
        .join("&"),
    ),
  ].join("&");

  const signingKey = `${percentEncode(consumerSecret)}&`;
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(signingKey),
    { name: "HMAC", hash: "SHA-1" },
    false,
    ["sign"],
  );
  const signatureBuffer = await crypto.subtle.sign("HMAC", cryptoKey, new TextEncoder().encode(baseString));
  const signature = btoa(String.fromCharCode(...new Uint8Array(signatureBuffer)));

  const headerParams: Record<string, string> = { ...oauthParams, oauth_signature: signature };

  return (
    "OAuth " +
    Object.keys(headerParams)
      .sort()
      .map((key) => `${key}="${percentEncode(headerParams[key])}"`)
      .join(", ")
  );
}

async function submitNewsLetter() {
  if (!emailAddress.value) {
    notifications.error({
      text: t("custom.newsletter.empty"),
      duration: 5000,
      single: true,
    });
    return;
  }

  const request_data = {
    url: "https://restapi.mailplus.nl/integrationservice/subscription/subscribe",
    method: "POST",
  };

  const authorizationHeader = await buildOAuthAuthorizationHeader(
    request_data.url,
    request_data.method,
    spotlerKey.value,
    spotlerSecret.value,
  );

  // Spotler body
  const body = {
    properties: {
      email: emailAddress.value,
    },
  };

  const spotlerApi = axios.create({
    baseURL: "https://restapi.mailplus.nl/integrationservice",
    headers: {
      Authorization: authorizationHeader,
      "Content-Type": "application/json",
    },
  });

  try {
    const response = await spotlerApi.post("/subscription/subscribe", body);

    if (response.status === 204) {
      notifications.success({
        text: t("custom.newsletter.success"),
        duration: 10000,
        single: true,
      });
      emailAddress.value = "";
    }
  } catch (error: any) {
    console.error("Spotler error response:", error.response?.data);
    notifications.error({
      text: error.response?.data?.message || "Er ging iets mis",
      duration: 10000,
      single: true,
    });
  }
}
</script>
